import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI, AdminUser } from '../../utils/api';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Loader from '../../components/Loader/Loader';
import './Admin.css';

type AdminTab = 'users' | 'subscriptions';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTimeLocal = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const Admin: React.FC = () => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [tab, setTab] = useState<AdminTab>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [expiryDrafts, setExpiryDrafts] = useState<Record<number, string>>({});
  const [topUpDrafts, setTopUpDrafts] = useState<Record<number, string>>({});

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await adminAPI.getUsers();
      if (response.success) {
        setUsers(response.users);
        const drafts: Record<number, string> = {};
        response.users.forEach((u) => {
          drafts[u.id] = formatDateTimeLocal(u.subscription_expires_at);
        });
        setExpiryDrafts(drafts);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка загрузки пользователей';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      loadUsers();
    }
  }, [authLoading, isAdmin, loadUsers]);

  const resolveExpiryForEnable = (userId: number): string | null => {
    const raw = expiryDrafts[userId];
    if (!raw) return null;
    const exp = new Date(raw);
    if (Number.isNaN(exp.getTime()) || exp.getTime() < Date.now()) {
      return null;
    }
    return raw;
  };

  const handleToggleSubscription = async (target: AdminUser) => {
    const nextActive = !target.has_subscription;
    setSavingId(target.id);
    setError('');
    try {
      const response = await adminAPI.updateSubscription(target.id, {
        subscription_active: nextActive,
        subscription_expires_at: nextActive ? resolveExpiryForEnable(target.id) : null,
      });
      if (response.success) {
        setUsers((prev) => prev.map((u) => (u.id === target.id ? response.user : u)));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка обновления подписки';
      setError(message);
    } finally {
      setSavingId(null);
    }
  };

  const handleTopUpBalance = async (target: AdminUser) => {
    const raw = topUpDrafts[target.id]?.trim();
    const amount = raw ? parseFloat(raw.replace(',', '.')) : NaN;
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Введите сумму пополнения больше 0');
      return;
    }
    setSavingId(target.id);
    setError('');
    try {
      const response = await adminAPI.updateBalance(target.id, { add: amount });
      if (response.success) {
        setUsers((prev) => prev.map((u) => (u.id === target.id ? response.user : u)));
        setTopUpDrafts((prev) => ({ ...prev, [target.id]: '' }));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка пополнения баланса';
      setError(message);
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveExpiry = async (target: AdminUser) => {
    if (!target.has_subscription) return;
    setSavingId(target.id);
    setError('');
    try {
      const response = await adminAPI.updateSubscription(target.id, {
        subscription_active: true,
        subscription_expires_at: expiryDrafts[target.id] || null,
      });
      if (response.success) {
        setUsers((prev) => prev.map((u) => (u.id === target.id ? response.user : u)));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка сохранения даты';
      setError(message);
    } finally {
      setSavingId(null);
    }
  };

  if (authLoading) {
    return <Loader fullScreen size="large" />;
  }

  if (!user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const activeCount = users.filter((u) => u.has_subscription).length;
  const inactiveCount = users.length - activeCount;

  return (
    <div className="admin-page">
      <Header />
      <main className="admin-main">
        <div className="container">
          <div className="admin-header">
            <h1 className="admin-header__title">Панель администратора</h1>
            <p className="admin-header__subtitle">
              Всего пользователей: {users.length} · с подпиской: {activeCount} · без подписки: {inactiveCount}
            </p>
          </div>

          <div className="admin-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'users'}
              className={`admin-tabs__btn${tab === 'users' ? ' admin-tabs__btn--active' : ''}`}
              onClick={() => setTab('users')}
            >
              Пользователи
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'subscriptions'}
              className={`admin-tabs__btn${tab === 'subscriptions' ? ' admin-tabs__btn--active' : ''}`}
              onClick={() => setTab('subscriptions')}
            >
              Управление подпиской
            </button>
          </div>

          {error && (
            <div className="admin-error" role="alert">
              {error}
            </div>
          )}

          <div className="admin-card">
            {loading ? (
              <Loader size="medium" />
            ) : tab === 'users' ? (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Имя</th>
                      <th>Email</th>
                      <th>Телефон</th>
                      <th>Роль</th>
                      <th>Подписка</th>
                      <th>Баланс</th>
                      <th>Регистрация</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.id}</td>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td>{u.phone || '—'}</td>
                        <td>
                          {u.is_admin ? (
                            <span className="admin-badge admin-badge--admin">Админ</span>
                          ) : (
                            <span className="admin-badge admin-badge--user">Пользователь</span>
                          )}
                        </td>
                        <td>
                          {u.has_subscription ? (
                            <span className="admin-badge admin-badge--active">Активна</span>
                          ) : (
                            <span className="admin-badge admin-badge--inactive">Нет</span>
                          )}
                        </td>
                        <td>{(u.balance ?? 0).toLocaleString('ru-RU')} ₽</td>
                        <td>{formatDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Пользователь</th>
                      <th>Email</th>
                      <th>Баланс</th>
                      <th>Статус</th>
                      <th>Действует до</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td>{(u.balance ?? 0).toLocaleString('ru-RU')} ₽</td>
                        <td>
                          {u.has_subscription ? (
                            <span className="admin-badge admin-badge--active">Подписка есть</span>
                          ) : (
                            <span className="admin-badge admin-badge--inactive">Нет подписки</span>
                          )}
                        </td>
                        <td>
                          <input
                            type="datetime-local"
                            className="admin-input"
                            value={expiryDrafts[u.id] ?? ''}
                            disabled={!u.has_subscription || savingId === u.id}
                            onChange={(e) =>
                              setExpiryDrafts((prev) => ({ ...prev, [u.id]: e.target.value }))
                            }
                          />
                        </td>
                        <td className="admin-actions">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            className="admin-input admin-input--narrow"
                            placeholder="Пополнить ₽"
                            value={topUpDrafts[u.id] ?? ''}
                            disabled={savingId === u.id}
                            onChange={(e) =>
                              setTopUpDrafts((prev) => ({ ...prev, [u.id]: e.target.value }))
                            }
                          />
                          <button
                            type="button"
                            className="admin-btn admin-btn--secondary"
                            disabled={savingId === u.id}
                            onClick={() => handleTopUpBalance(u)}
                          >
                            Пополнить
                          </button>
                          <button
                            type="button"
                            className={`admin-btn${u.has_subscription ? ' admin-btn--danger' : ' admin-btn--primary'}`}
                            disabled={savingId === u.id}
                            onClick={() => handleToggleSubscription(u)}
                          >
                            {savingId === u.id
                              ? 'Сохранение…'
                              : u.has_subscription
                                ? 'Отключить'
                                : 'Включить'}
                          </button>
                          {u.has_subscription && (
                            <button
                              type="button"
                              className="admin-btn admin-btn--secondary"
                              disabled={savingId === u.id}
                              onClick={() => handleSaveExpiry(u)}
                            >
                              Сохранить дату
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Admin;
